import * as React from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { Check, ChevronRight, Circle } from "lucide-react";

import { cn } from "../lib/utils";

const DropdownMenu = DropdownMenuPrimitive.Root;

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuGroup = DropdownMenuPrimitive.Group;

const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuSub = DropdownMenuPrimitive.Sub;

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean;
  }
>(({ className, inset, children, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(
      "tw-figr-flex tw-figr-cursor-default tw-figr-select-none tw-figr-items-center  tw-figr-rounded-s-m tw-figr-px-2 tw-figr-py-1.5 tw-figr-text-sm tw-figr-outline-none focus:tw-figr-bg-neutral-500 data-[state=open]:tw-figr-bg-neutral-500",
      inset && "tw-figr-pl-8",
      className
    )}
    {...props}
  >
    {children}
    <ChevronRight className="tw-figr-ml-auto tw-figr-h-4 tw-figr-w-4" />
  </DropdownMenuPrimitive.SubTrigger>
));
DropdownMenuSubTrigger.displayName =
  DropdownMenuPrimitive.SubTrigger.displayName;

const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.SubContent
    ref={ref}
    className={cn(
      "tw-figr-z-50 tw-figr-min-w-[8rem] tw-figr-border-collapse tw-figr-overflow-hidden tw-figr-rounded-md tw-figr-bg-base-white tw-figr-p-1 tw-figr-text-base-black tw-figr-shadow-lg data-[state=open]:tw-figr-animate-in data-[state=closed]:tw-figr-animate-out data-[state=closed]:tw-figr-fade-out-0 data-[state=open]:tw-figr-fade-in-0 data-[state=closed]:tw-figr-zoom-out-95 data-[state=open]:tw-figr-zoom-in-95 data-[side=bottom]:tw-figr-slide-in-from-top-2 data-[side=left]:tw-figr-slide-in-from-right-2 data-[side=right]:tw-figr-slide-in-from-left-2 data-[side=top]:tw-figr-slide-in-from-bottom-2",
      className
    )}
    {...props}
  />
));
DropdownMenuSubContent.displayName =
  DropdownMenuPrimitive.SubContent.displayName;

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "tw-figr-z-50 tw-figr-min-w-[8rem] tw-figr-border-collapse tw-figr-overflow-hidden tw-figr-rounded-md tw-figr-bg-base-white tw-figr-p-1 tw-figr-text-base-black tw-figr-shadow-md data-[state=open]:tw-figr-animate-in data-[state=closed]:tw-figr-animate-out data-[state=closed]:tw-figr-fade-out-0 data-[state=open]:tw-figr-fade-in-0 data-[state=closed]:tw-figr-zoom-out-95 data-[state=open]:tw-figr-zoom-in-95 data-[side=bottom]:tw-figr-slide-in-from-top-2 data-[side=left]:tw-figr-slide-in-from-right-2 data-[side=right]:tw-figr-slide-in-from-left-2 data-[side=top]:tw-figr-slide-in-from-bottom-2",
        className
      )}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      "tw-figr-relative tw-figr-flex tw-figr-cursor-default tw-figr-select-none tw-figr-items-center tw-figr-rounded-sm tw-figr-px-2 tw-figr-py-1.5 tw-figr-text-sm tw-figr-outline-none tw-figr-transition-colors focus:tw-figr-bg-neutral-500 focus:tw-figr-text-base-black data-[disabled]:tw-figr-pointer-events-none data-[disabled]:tw-figr-opacity-50",
      inset && "tw-figr-pl-8",
      className
    )}
    {...props}
  />
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ className, children, checked, ...props }, ref) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    className={cn(
      "tw-figr-relative  tw-figr-flex tw-figr-cursor-default tw-figr-select-none tw-figr-items-center tw-figr-rounded-sm tw-figr-py-1.5 tw-figr-pl-8 tw-figr-pr-2 tw-figr-text-sm tw-figr-outline-none tw-figr-transition-colors focus:tw-figr-bg-neutral-500 focus:tw-figr-text-base-black data-[disabled]:tw-figr-pointer-events-none data-[disabled]:tw-figr-opacity-50",
      className
    )}
    checked={checked}
    {...props}
  >
    <span className="tw-figr-absolute tw-figr-left-2 tw-figr-flex tw-figr-h-3.5 tw-figr-w-3.5  tw-figr-items-center tw-figr-justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Check className="tw-figr-h-4 tw-figr-w-4" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.CheckboxItem>
));
DropdownMenuCheckboxItem.displayName =
  DropdownMenuPrimitive.CheckboxItem.displayName;

const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    className={cn(
      "tw-figr-relative  tw-figr-flex tw-figr-cursor-default tw-figr-select-none tw-figr-items-center tw-figr-rounded-sm tw-figr-py-1.5 tw-figr-pl-8 tw-figr-pr-2 tw-figr-text-sm tw-figr-outline-none tw-figr-transition-colors focus:tw-figr-bg-neutral-500 focus:tw-figr-text-base-black data-[disabled]:tw-figr-pointer-events-none data-[disabled]:tw-figr-opacity-50",
      className
    )}
    {...props}
  >
    <span className="tw-figr-absolute tw-figr-left-2 tw-figr-flex tw-figr-h-3.5 tw-figr-w-3.5  tw-figr-items-center tw-figr-justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Circle className="tw-figr-h-2 tw-figr-w-2 tw-figr-fill-current" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.RadioItem>
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn(
      "tw-figr-px-2 tw-figr-py-1.5 tw-figr-text-sm tw-figr-font-semibold",
      inset && "tw-figr-pl-8",
      className
    )}
    {...props}
  />
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn(
      "-tw-figr-mx-1 tw-figr-my-1 tw-figr-h-px tw-figr-bg-neutral-200",
      className
    )}
    {...props}
  />
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

const DropdownMenuShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn(
        "tw-figr-ml-auto tw-figr-text-xs tw-figr-tracking-widest tw-figr-opacity-60",
        className
      )}
      {...props}
    />
  );
};
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup
};
